import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UserPlus } from "lucide-react";
import { apiClient } from "../../utils/apiClient/index.ts";
import { useAlert } from "../../hooks/useAlert";
import type { Case, OrganizationUser, CaseAssignment } from "../../types";
import Button from "../../components/common/ui/button/Button";
import Label from "../../components/common/form/Label";
import Breadcrumb from "../../components/common/common/Breadcrumb";

export const AssignUserPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setAlert } = useAlert();

  const caseId = searchParams.get("caseId");
  const [caseItem, setCaseItem] = useState<Case | null>(null);
  const [orgMembers, setOrgMembers] = useState<OrganizationUser[]>([]);
  const [caseAssignments, setCaseAssignments] = useState<CaseAssignment[]>([]);
  const [loading, setLoading] = useState(false);

  // Form state
  const [assignUserId, setAssignUserId] = useState("");

  useEffect(() => {
    if (caseId) {
      loadCase();
      loadCaseAssignments();
    }
  }, [caseId]);

  const loadCase = async () => {
    if (!caseId) return;

    try {
      // Get the case by fetching all cases (API limitation workaround)
      const orgs = await apiClient.listOrganizations();
      for (const org of orgs) {
        const cases = await apiClient.listCases(org.id);
        const foundCase = cases.find((c) => c.id === caseId);
        if (foundCase) {
          setCaseItem(foundCase);
          loadOrgMembers(foundCase.organizationId);
          return;
        }
      }

      setAlert({
        variant: "error",
        title: "Error",
        message: "Case not found",
      });
      navigate("/cases");
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to load case",
      });
      navigate("/cases");
    }
  };

  const loadOrgMembers = async (orgId: string) => {
    try {
      const members = await apiClient.listOrganizationMembers(orgId);
      setOrgMembers(members);
    } catch (error) {
      console.error("Failed to load org members:", error);
    }
  };

  const loadCaseAssignments = async () => {
    if (!caseId) return;

    try {
      const assignments = await apiClient.getCaseAssignments(caseId);
      setCaseAssignments(assignments);
    } catch (error) {
      console.error("Failed to load assignments:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!caseItem) return;

    setLoading(true);
    try {
      await apiClient.assignUserToCase(caseItem.id, {
        userId: assignUserId,
      });
      setAlert({
        variant: "success",
        title: "Success",
        message: "User assigned successfully",
      });
      navigate("/cases");
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message:
          error instanceof Error ? error.message : "Failed to assign user",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/cases");
  };

  const availableMembers = orgMembers.filter(
    (member) =>
      !caseAssignments.some(
        (assignment) => assignment.userId === member.userId,
      ),
  );

  return (
    <div className="space-y-6">
      <Breadcrumb
        items={[{ label: "Cases", path: "/cases" }, { label: "Assign User" }]}
      />

      <div className="flex items-center gap-3">
        <UserPlus size={32} className="text-brand-500" />
        <div>
          <h1 className="text-title-md2 font-semibold text-black dark:text-white">
            Assign User to Case
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {caseItem ? `Assigning user to "${caseItem.title}"` : "Loading..."}
          </p>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label>
              Select User <span className="text-error-500">*</span>
            </Label>
            <select
              value={assignUserId}
              onChange={(e) => setAssignUserId(e.target.value)}
              required
              className="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            >
              <option value="">Choose a user...</option>
              {availableMembers.map((member) => (
                <option key={member.userId} value={member.userId}>
                  {member.user?.fullName || member.user?.email}
                </option>
              ))}
            </select>
            {availableMembers.length === 0 && orgMembers.length > 0 && (
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                All organization members are already assigned to this case
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !caseItem || availableMembers.length === 0}
            >
              {loading ? "Assigning..." : "Assign User"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
